import { FloatingPortal, type Placement } from '@floating-ui/react';
import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react'; // If FocusTrap is used for other "full-screen" UIs, consider splitting the overlay + FocusTrap into its own component
import { type JSX, useRef } from 'react';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import useOffClick from '@alltrails/core/hooks/useOffClick';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';
import usePopover from './usePopover';

type PopoverProps = {
  anchor: JSX.Element;
  children: React.ReactNode;
  className?: string;
  containerMouseHandlers?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  };
  'data-denali'?: string;
  placement?: Extract<Placement, 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end'>;
  isOpen?: boolean;
  onCloseRequest: () => void;
  returnFocusOnDeactivate?: boolean;
  isMobile?: boolean;
  // Render the popover in a portal so it can escape ancestors that clip overflow (example: Modal)
  usePortal?: boolean;
};

const Popover = ({
  anchor,
  children,
  className,
  containerMouseHandlers,
  'data-denali': dataDenali,
  placement = 'bottom-start',
  isOpen = false,
  onCloseRequest,
  isMobile = false,
  usePortal = false
}: PopoverProps) => {
  const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen);
  const containerRef = useRef<HTMLDivElement>(null);

  const { anchorElement, popoverRef, popoverStyle, popoverInteractiveProps, floatingRef } = usePopover({
    anchor,
    placement,
    strategy: usePortal ? 'fixed' : undefined
  });

  useOffClick({ engaged: isOpen, callback: onCloseRequest, container: containerRef, additionalContainer: usePortal ? floatingRef : undefined });

  // useGroupHover, along with a little css, is needed to avoid an issue where hovering in between the anchor and the popover can cause
  // the popover to repeatedly open and close since onMouseEnter is triggered as the popover animates out.
  const { onMouseEnter, onMouseLeave } = useGroupHover(containerMouseHandlers?.onMouseEnter, containerMouseHandlers?.onMouseLeave);

  const floatingElement = (
    <FocusTrap
      active={usePortal ? isAnimating || isVisible : isVisible}
      // don't use clickOutsideDeactivates because it can lead to weird issues with clicking on the anchor while the popover is open
      // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
      focusTrapOptions={{
        allowOutsideClick: true,
        clickOutsideDeactivates: false,
        escapeDeactivates: true,
        onDeactivate: onCloseRequest,
        fallbackFocus: '#popover',
        initialFocus: false
      }}
    >
      <div
        ref={popoverRef}
        {...popoverInteractiveProps}
        className={classNames(
          styles.popper,
          styles[placement.split('-')[0]],
          {
            [styles.isAnimating]: isAnimating,
            [styles.isVisible]: isVisible,
            [styles.portaled]: usePortal
          },
          className
        )}
        id="popover"
        onAnimationEnd={onAnimationEnd}
        style={popoverStyle}
      >
        {children}
      </div>
    </FocusTrap>
  );

  return (
    <div ref={containerRef} {...setDenaliAttribute(dataDenali || 'Popover')} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {anchorElement}
      {(isAnimating || isVisible || isMobile) && (usePortal ? <FloatingPortal>{floatingElement}</FloatingPortal> : floatingElement)}
    </div>
  );
};

export default Popover;
